/**
 * Celebration timings — single source of truth for all 4 tier durations
 * + tablet multipliers + reduced-motion override (Plan 02-15 / D-10 /
 * CEL-02 / CEL-03 / UI-SPEC §"Animation timing constants").
 *
 * The shape below is verbatim from UI-SPEC. Every tier component reads
 * from this module rather than hard-coding numbers — adding a new
 * surface or changing a duration is a single-file edit.
 *
 * `computeForTier(tier, viewport, reducedMotion)` is the helper consumers
 * call at render time. It applies:
 *   - tablet 1.2× multiplier to durations (CEL-03)
 *   - tablet 2× multiplier to confetti counts (CEL-03)
 *   - reduced-motion override (durations=0, confettiCount=0,
 *     soundMuted=true) — INV-RM-1 part 1
 *
 * Reduced-motion ALWAYS wins over tablet — a tablet user with
 * `prefers-reduced-motion: reduce` gets the static fallback.
 */
export const TIMINGS = {
  l1: {
    scale: { from: 0.96, to: 1, duration: 220 },
    checkOverlay: 220,
  },
  l2: {
    glow: 600,
    checkBounce: { stiffness: 360, damping: 24, mass: 0.7 },
  },
  l3: {
    confettiCount: { phone: 60, tablet: 140 },
    confettiDuration: 1200,
    countup: 700,
    // Hvor lenge WalletRoute holder en count-up som venter på at saldoen
    // skal nå `to` (#79). Var toastens levetid før #155, og verdien beholdes.
    countupIntentTtl: 3000,
    // #155: L3 er en hel skjerm som blir stående til «Fortsett», ikke en
    // toast i 3 s; poengskiven spretter inn med samme fjær som L4-merket.
    discPop: {
      from: 0.6,
      to: 1,
      spring: { stiffness: 240, damping: 22, mass: 0.9 },
    },
  },
  l4: {
    scrimFadeIn: 200,
    badgeZoom: {
      from: 0.6,
      to: 1,
      spring: { stiffness: 240, damping: 22, mass: 0.9 },
    },
    titleFadeIn: 250,
    titleDelay: 400,
    confettiCount: { phone: 160, tablet: 320 },
    confettiDuration: 1200,
    confettiDelay: 400,
    autoDismiss: 5000,
    scrimFadeOut: 240,
  },
  tabletMultiplier: 1.2,
  tabletConfettiMultiplier: 2.0,
  reducedMotion: {
    durations: 0,
    confettiCount: 0,
    soundMuted: true,
  },
} as const;

export type Tier = 'l1' | 'l2' | 'l3' | 'l4';
export type ViewportClass = 'phone' | 'tablet';

export interface TierResolution {
  /** Whether the user's `prefers-reduced-motion: reduce` has flattened all motion. */
  reducedMotion: boolean;
  /**
   * Number of confetti particles to fire. 0 for tiers without confetti
   * (l1, l2) and for any tier under reduced motion.
   */
  confettiCount: number;
  /**
   * Duration (ms) of the tier's primary confetti burst. 0 for non-confetti
   * tiers and for reduced-motion users. For tablet users, the tier's
   * native duration is multiplied by `tabletMultiplier`.
   */
  confettiDuration: number;
  /** True when SFX must be silenced (reduced-motion users). */
  soundMuted: boolean;
}

/**
 * Resolve the runtime configuration for a single tier given the user's
 * viewport class and reduced-motion preference.
 *
 * - reduced-motion users get a static all-zero result + soundMuted=true
 * - tablet users get 2× confetti and 1.2× duration
 * - phone users get the base config
 *
 * Tiers without confetti (l1, l2) return 0/0 for the confetti fields
 * regardless of viewport — only l3 and l4 fire confetti per UI-SPEC §11.
 */
export function computeForTier(
  tier: Tier,
  viewport: ViewportClass,
  reducedMotion: boolean,
): TierResolution {
  if (reducedMotion) {
    return {
      reducedMotion: true,
      confettiCount: 0,
      confettiDuration: 0,
      soundMuted: true,
    };
  }

  if (tier === 'l3' || tier === 'l4') {
    const cfg = TIMINGS[tier];
    const baseCount = cfg.confettiCount[viewport];
    const baseDuration = cfg.confettiDuration;
    const durationMultiplier =
      viewport === 'tablet' ? TIMINGS.tabletMultiplier : 1;
    return {
      reducedMotion: false,
      confettiCount: baseCount,
      confettiDuration: Math.round(baseDuration * durationMultiplier),
      soundMuted: false,
    };
  }

  // l1 / l2 — no confetti.
  return {
    reducedMotion: false,
    confettiCount: 0,
    confettiDuration: 0,
    soundMuted: false,
  };
}
